import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { clsx } from 'clsx';
import { CheckCircle2, RotateCcw, Sparkles } from 'lucide-react';
import {
  clearActivityScore,
  readWorkbookJson,
  writeActivityScore,
  writeWorkbookJson,
  type WorkbookActivityCompletion,
} from './workbookPersist';

export type CaseSuffixFillExample = {
  subject: string;
  answer: string;
  predicate: string;
  hint: string;
};

export type CaseSuffixFillItem = {
  q_no: number;
  subject: string;
  predicate: string;
  hint: string;
  options: string[];
  correct_answer: string;
};

export interface CaseSuffixFillCardProps {
  workbookLabel?: string;
  title: string;
  instructions?: string;
  example: CaseSuffixFillExample;
  items: CaseSuffixFillItem[];
  sourcePage?: number;
  persistenceId?: string;
  onInteracted?: () => void;
  onWorkbookStatusChange?: (status: WorkbookActivityCompletion) => void;
}

type PersistShape = { selected: Record<number, string> };

function normalizeTamilAnswer(s: string): string {
  let t = String(s).trim().replace(/\s+/g, ' ');
  try {
    t = t.normalize('NFKC');
  } catch {
    /* ignore */
  }
  t = t.replace(/^[\s.,;:!?'"()[\]{}]+|[\s.,;:!?'"()[\]{}]+$/gu, '');
  return t.trim();
}

function rowOutcome(selected: string, correctAnswer: string): 'none' | 'correct' | 'wrong' {
  const s = normalizeTamilAnswer(selected);
  if (!s) return 'none';
  return normalizeTamilAnswer(s) === normalizeTamilAnswer(correctAnswer) ? 'correct' : 'wrong';
}

export const CaseSuffixFillCard: React.FC<CaseSuffixFillCardProps> = ({
  workbookLabel,
  title,
  instructions,
  example,
  items,
  sourcePage,
  persistenceId,
  onInteracted,
  onWorkbookStatusChange,
}) => {
  const storageKey = persistenceId ? `g3-csf-${persistenceId}` : '';

  const itemsRef = useRef(items);
  itemsRef.current = items;
  /** Stable when the activity’s question set is unchanged; avoids re-hydrating on every parent re-render (new `items` array reference). */
  const itemsStableKey = items
    .map((it) => it.q_no)
    .sort((a, b) => a - b)
    .join('|');

  const initialSelected = useMemo(() => {
    const v: Record<number, string> = {};
    for (const it of items) v[it.q_no] = '';
    return v;
  }, [items]);

  const [selected, setSelected] = useState<Record<number, string>>(initialSelected);

  useEffect(() => {
    const list = itemsRef.current;
    const empty: Record<number, string> = {};
    for (const it of list) empty[it.q_no] = '';

    if (!storageKey) {
      setSelected(empty);
      return;
    }
    const loaded = readWorkbookJson<Partial<PersistShape> & { values?: Record<string, unknown> } | null>(
      storageKey,
      null
    );
    const merged = { ...empty };
    if (loaded?.selected && typeof loaded.selected === 'object') {
      for (const k of Object.keys(loaded.selected)) {
        const q = Number(k);
        if (!Number.isFinite(q) || merged[q] === undefined) continue;
        merged[q] = String((loaded.selected as Record<string, unknown>)[k] ?? '');
      }
    } else if (loaded && 'values' in loaded && typeof (loaded as { values?: unknown }).values === 'object') {
      const vals = (loaded as { values: Record<string, unknown> }).values;
      for (const k of Object.keys(vals)) {
        const q = Number(k);
        if (!Number.isFinite(q) || merged[q] === undefined) continue;
        merged[q] = String(vals[k] ?? '');
      }
    }
    setSelected(merged);
  }, [storageKey, itemsStableKey]);

  useEffect(() => {
    if (!storageKey) return;
    writeWorkbookJson(storageKey, { selected } satisfies PersistShape);
  }, [storageKey, selected]);

  const mark = useCallback(() => onInteracted?.(), [onInteracted]);

  const pickOption = (qNo: number, option: string) => {
    mark();
    setSelected((prev) => {
      if (normalizeTamilAnswer(prev[qNo] ?? '').length > 0) return prev;
      return { ...prev, [qNo]: option };
    });
  };

  const resetAll = () => {
    mark();
    setSelected(initialSelected);
    if (storageKey) writeWorkbookJson(storageKey, { selected: initialSelected } satisfies PersistShape);
    if (persistenceId) clearActivityScore(persistenceId);
    onWorkbookStatusChange?.('not_started');
  };

  useEffect(() => {
    if (!onWorkbookStatusChange || items.length === 0) {
      onWorkbookStatusChange?.('not_started');
      return;
    }
    const allFilled = items.every((it) => normalizeTamilAnswer(selected[it.q_no] ?? '').length > 0);
    const anyPick = items.some((it) => normalizeTamilAnswer(selected[it.q_no] ?? '').length > 0);

    /** Activity score = correct / total for the whole sheet; "completed" when every row has a locked pick (popup + next). */
    if (allFilled && items.length > 0) {
      if (persistenceId) {
        const correct = items.filter(
          (it) => rowOutcome(selected[it.q_no] ?? '', it.correct_answer) === 'correct'
        ).length;
        writeActivityScore(persistenceId, (correct / items.length) * 100);
      }
      onWorkbookStatusChange('completed');
      return;
    }
    if (anyPick) {
      onWorkbookStatusChange('in_progress');
      return;
    }
    onWorkbookStatusChange('not_started');
  }, [onWorkbookStatusChange, items, selected, persistenceId]);

  const shell = (body: React.ReactNode, footer: React.ReactNode) => (
    <div className="overflow-hidden rounded-[2rem] border-4 border-amber-200/90 bg-gradient-to-br from-amber-50 via-orange-50/80 to-rose-50/60 shadow-[0_20px_50px_-12px_rgba(217,119,6,0.28)] dark:border-amber-800/50 dark:from-amber-950/35 dark:via-slate-900 dark:to-slate-900">
      <div className="border-b border-amber-200/70 bg-white/55 px-5 py-4 backdrop-blur-sm dark:border-amber-900/40 dark:bg-slate-900/50">
        <div className="flex flex-wrap items-start justify-between gap-2">
          <div>
            {workbookLabel ? (
              <p className="text-sm font-bold text-amber-800 tamil-text dark:text-amber-200" lang="ta">
                {workbookLabel}
              </p>
            ) : null}
            <h2 className="mt-1 text-2xl font-extrabold leading-snug text-amber-950 tamil-text md:text-3xl dark:text-amber-50" lang="ta">
              {title}
            </h2>
            {instructions ? (
              <p className="mt-3 text-lg font-semibold leading-relaxed text-red-700 tamil-text dark:text-red-300/90" lang="ta">
                {instructions}
              </p>
            ) : null}
          </div>
          {sourcePage != null ? (
            <span className="rounded-full bg-amber-700 px-3 py-1 text-sm font-bold text-white shadow-md">p.{sourcePage}</span>
          ) : null}
        </div>
      </div>
      <div className="px-4 py-6 sm:px-6">{body}</div>
      <div className="flex flex-wrap items-center justify-between gap-3 border-t border-amber-200/60 bg-white/45 px-4 py-4 backdrop-blur-sm dark:border-amber-900/40 dark:bg-slate-900/50 sm:px-6">
        {footer}
      </div>
    </div>
  );

  const exampleSentence = (
    <>
      <span className="text-amber-950 dark:text-amber-50">{example.subject}</span>{' '}
      <span className="mx-1 inline-block rounded-lg border-2 border-amber-400/80 bg-white px-3 py-1 text-xl font-black text-amber-900 shadow-inner dark:border-amber-600 dark:bg-amber-950/40 dark:text-amber-100 sm:text-2xl">
        {example.answer}
      </span>{' '}
      <span className="text-amber-950 dark:text-amber-50">{example.predicate}</span>
    </>
  );

  const allCorrectNow = useMemo(
    () => items.length > 0 && items.every((it) => rowOutcome(selected[it.q_no] ?? '', it.correct_answer) === 'correct'),
    [items, selected]
  );

  const footer = (
    <div className="flex w-full flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center sm:justify-between">
      <button
        type="button"
        onClick={resetAll}
        className="inline-flex min-h-[52px] items-center gap-2 rounded-2xl border-2 border-amber-500 bg-white px-5 py-3 text-base font-bold text-amber-900 shadow-sm transition hover:bg-amber-50 dark:border-amber-600 dark:bg-slate-800 dark:text-amber-100 dark:hover:bg-slate-700 tamil-text"
        lang="ta"
      >
        <RotateCcw className="h-5 w-5 shrink-0" aria-hidden />
        மீண்டும் தொடங்கு
      </button>
      {allCorrectNow ? (
        <p className="flex items-center justify-center gap-2 text-center text-lg font-black text-emerald-700 tamil-text dark:text-emerald-300 sm:justify-end" lang="ta">
          <CheckCircle2 className="h-7 w-7 shrink-0 text-emerald-600" aria-hidden />
          அனைத்தும் சரி!
        </p>
      ) : (
        <p className="text-center text-sm font-bold text-amber-900/90 tamil-text dark:text-amber-100/90 sm:text-right" lang="ta">
          கீழுள்ள மூன்று விடைகளில் ஒன்றைத் தட்டி தெரிவு செய்யவும்.
        </p>
      )}
    </div>
  );

  return shell(
    <div className="space-y-6">
      <section
        className="rounded-2xl border-4 border-amber-400/70 bg-gradient-to-br from-amber-100 via-orange-100/90 to-amber-50 p-5 shadow-inner dark:border-amber-600 dark:from-amber-950/60 dark:via-orange-950/40 dark:to-amber-950/30"
        aria-label="எடுத்துக்காட்டு"
      >
        <div className="mb-3 flex flex-wrap items-center gap-2">
          <Sparkles className="h-6 w-6 shrink-0 text-amber-700 dark:text-amber-300" aria-hidden />
          <h3 className="text-lg font-black uppercase tracking-wide text-amber-900 tamil-text dark:text-amber-100" lang="ta">
            எடுத்துக்காட்டு
          </h3>
        </div>
        <p className="text-xl font-black leading-relaxed text-amber-950 tamil-text sm:text-2xl dark:text-amber-50" lang="ta">
          {exampleSentence}
        </p>
        <div className="mt-4 inline-flex rounded-xl border-2 border-amber-500/50 bg-white/90 px-4 py-2 text-base font-bold text-amber-900 shadow-sm dark:border-amber-700 dark:bg-slate-900/80 dark:text-amber-100 tamil-text" lang="ta">
          குறிப்பு: {example.hint}
        </div>
      </section>

      <div className="space-y-5">
        <h3 className="text-center text-base font-black text-amber-900 tamil-text dark:text-amber-100" lang="ta">
          உன் விடைகள்
        </h3>
        {items.map((it) => {
          const sel = selected[it.q_no] ?? '';
          const outcome = rowOutcome(sel, it.correct_answer);
          return (
            <div
              key={it.q_no}
              className={clsx(
                'rounded-2xl border-4 p-4 shadow-md transition sm:p-5',
                outcome === 'correct'
                  ? 'border-emerald-500 bg-emerald-50/90 dark:border-emerald-500 dark:bg-emerald-950/35'
                  : outcome === 'wrong'
                    ? 'border-rose-500 bg-rose-50/90 dark:border-rose-500 dark:bg-rose-950/35'
                    : 'border-white/90 bg-white/85 dark:border-slate-600 dark:bg-slate-800/85'
              )}
              role="group"
              aria-label={`வினா ${it.q_no}`}
            >
              <div className="flex flex-wrap items-center gap-x-2 gap-y-3 text-lg font-black leading-snug text-slate-900 tamil-text sm:text-xl dark:text-slate-50" lang="ta">
                <span className="shrink-0 rounded-lg bg-amber-200/80 px-2 py-0.5 text-sm text-amber-950 dark:bg-amber-900/50 dark:text-amber-100">
                  {it.q_no}.
                </span>
                <span>{it.subject}</span>
                <span
                  className={clsx(
                    'mx-1 inline-flex min-h-[52px] min-w-[6.5rem] max-w-[14rem] items-center justify-center rounded-xl border-2 border-dashed px-3 py-2 text-center text-xl font-black shadow-inner sm:min-w-[8rem] sm:text-2xl',
                    outcome === 'correct'
                      ? 'border-emerald-500 bg-white text-emerald-900 dark:border-emerald-400 dark:bg-emerald-950/50 dark:text-emerald-50'
                      : outcome === 'wrong'
                        ? 'border-rose-500 bg-white text-rose-900 dark:border-rose-400 dark:bg-rose-950/40 dark:text-rose-50'
                        : 'border-amber-400/80 bg-amber-50/50 text-amber-800/70 dark:border-amber-600 dark:bg-amber-950/30 dark:text-amber-200/80'
                  )}
                  aria-live="polite"
                >
                  {sel ? (
                    <span className="tamil-text">{sel}</span>
                  ) : (
                    <span className="text-base font-bold tracking-widest text-amber-700/50 dark:text-amber-300/50">———</span>
                  )}
                </span>
                <span>{it.predicate}</span>
              </div>

              <div className="mt-3 inline-flex rounded-xl border border-app-primary/20/90 bg-app-primary/10/90 px-3 py-1.5 text-sm font-bold text-app-primary dark:border-violet-700 dark:bg-app-primary/15/40 dark:text-violet-100 tamil-text" lang="ta">
                குறிப்பு: {it.hint}
              </div>

              <div className="mt-4 flex flex-wrap gap-2 sm:gap-3" role="list">
                {it.options.map((opt, oi) => {
                  const isChosen = normalizeTamilAnswer(sel) === normalizeTamilAnswer(opt);
                  const rowLocked = normalizeTamilAnswer(sel).length > 0;
                  return (
                    <button
                      key={`${it.q_no}-${oi}`}
                      type="button"
                      role="listitem"
                      disabled={rowLocked && !isChosen}
                      onClick={() => pickOption(it.q_no, opt)}
                      className={clsx(
                        'min-h-[52px] flex-1 rounded-2xl border-4 px-4 py-3 text-lg font-black shadow-md transition active:scale-[0.98] tamil-text sm:min-w-[140px] sm:flex-none sm:text-xl',
                        isChosen && outcome === 'correct'
                          ? 'border-emerald-600 bg-emerald-100 text-emerald-950 ring-2 ring-emerald-400 dark:border-emerald-400 dark:bg-emerald-900/50 dark:text-emerald-50'
                          : isChosen && outcome === 'wrong'
                            ? 'border-rose-600 bg-rose-100 text-rose-950 ring-2 ring-rose-400 dark:border-rose-400 dark:bg-rose-900/45 dark:text-rose-50'
                            : 'border-amber-200/90 bg-gradient-to-br from-white to-amber-50/80 text-amber-950 hover:border-amber-400 hover:brightness-[1.02] dark:border-slate-600 dark:from-slate-800 dark:to-slate-800 dark:text-amber-100 dark:hover:border-amber-500'
                      )}
                      aria-pressed={isChosen}
                    >
                      {opt}
                    </button>
                  );
                })}
              </div>

              {outcome === 'correct' ? (
                <p className="mt-3 text-sm font-black text-emerald-700 tamil-text dark:text-emerald-300" lang="ta">
                  சரி!
                </p>
              ) : null}
            </div>
          );
        })}
      </div>
    </div>,
    footer
  );
};
